import { forwardRef } from 'react'

export interface DateInputProps {
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  required?: boolean
  className?: string
  disabled?: boolean
  placeholder?: string
}

/** Converte "dd/mm/aaaa" em "aaaa-mm-dd". Se nao tiver 8 digitos, retorna string vazia. */
export function toISODate(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length !== 8) return ''
  const dd = digits.slice(0, 2)
  const mm = digits.slice(2, 4)
  const yyyy = digits.slice(4, 8)
  const d = parseInt(dd, 10)
  const m = parseInt(mm, 10)
  const y = parseInt(yyyy, 10)
  if (d < 1 || d > 31 || m < 1 || m > 12 || y < 1900 || y > 2100) return ''
  return `${yyyy}-${mm}-${dd}`
}

/** Converte ISO (aaaa-mm-dd ou aaaa-mm-ddT...) em dd/mm/aaaa */
export function toBrDate(iso: string): string {
  if (!iso) return ''
  const part = iso.includes('T') ? iso.split('T')[0] : iso
  const p = part.split('-')
  if (p.length !== 3) return ''
  const dd = p[2].padStart(2, '0')
  const mm = p[1].padStart(2, '0')
  return `${dd}/${mm}/${p[0]}`
}

/** Aplica mascara dd/mm/aaaa sobre string de digitos (ou qualquer string) */
export function applyDateMask(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`
}

/** Detecta se valor esta em formato ISO (aaaa-mm-dd) */
function isISODate(val: string): boolean {
  if (!val || val.length < 10) return false
  const p = val.includes('T') ? val.split('T')[0].split('-') : val.split('-')
  return p.length === 3 && p[0].length === 4
}

/**
 * Input de data com mascara dd/mm/aaaa.
 *
 * Recebe  value: ISO "aaaa-mm-dd"  ou  br "dd/mm/aaaa"  (nos 10 caracteres).
 * Exibe sempre "dd/mm/aaaa".
 * Quando o usuario digita, envia "dd/mm/aaaa" no onChange.
 */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  function DateInput({ value = '', onChange, onBlur, required, className, disabled, placeholder = 'dd/mm/aaaa' }, ref) {

    // Determina o que mostrar: ISO -> dd/mm/aaaa; ja br -> aplica mascara
    const rawValue = value || ''
    const display = isISODate(rawValue) ? toBrDate(rawValue) : applyDateMask(rawValue)

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const masked = applyDateMask(e.target.value)
      onChange?.(masked)
    }

    const handleBlur = () => {
      onBlur?.()
    }

    return (
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        maxLength={10}
        placeholder={placeholder}
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        required={required}
        disabled={disabled}
        className={className}
        autoComplete="bday"
      />
    )
  }
)
